// FILE: src/components/app/bases/BudgetBaseComponent/BudgetCard/BudgetCard.js
import React from "react";
import PropTypes from "prop-types";
import styles from "./BudgetCard.module.css";

const fmtMoney = (n, code = "USD") =>
  Number(n).toLocaleString(undefined, { style: "currency", currency: code });

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export default function BudgetCard({
  item,
  plannedTransactions = [],
  accounts = [],
  expanded = false,
  onToggle,
  onDelete,
  onAddPlannedTx,
  onDeletePlannedTx,
}) {
  const [draft, setDraft] = React.useState({ description: "", date: "", amount: "" });

  const currencyCode =
    item?.source?.type === "account"
      ? (accounts.find((a) => String(a.id) === String(item?.source?.accountId))?.currencyCode || "USD")
      : "USD";

  const accountName =
    item?.source?.type === "account"
      ? (accounts.find((a) => String(a.id) === String(item?.source?.accountId))?.name || "Selected account")
      : "Net Worth (general)";

  // 🔑 Compute progress from the plannedTransactions prop (fallback to _computed if empty)
  const allocatedFromProp = plannedTransactions.reduce((s, t) => {
    const abs = num(t?.amountAbs);
    const signed = num(t?.amount);
    return s + (abs ? Math.abs(abs) : Math.abs(signed));
  }, 0);

  const allocated =
    plannedTransactions.length > 0
      ? allocatedFromProp
      : num(item?._computed?.allocated);

  const total = num(item?.amount);
  const remaining = Math.max(0, total - allocated);
  const bufferMax = total * (1 + (num(item?.bufferPct) || 0) / 100);
  const percent = total > 0 ? Math.min(100, Math.max(0, (allocated / total) * 100)) : 0;

  const barVariant =
    allocated > total ? "danger" : percent >= 85 ? "warning" : "success";

  const days = item?._computed?.days ?? 0;
  const daysLabel = days >= 0 ? `${days} day${days === 1 ? "" : "s"} left` : "date passed";

  const addTx = () => {
    const amount = num(draft.amount);
    if (!draft.description || !draft.date || amount <= 0) return;
    onAddPlannedTx?.(item.id, { description: draft.description, date: draft.date, amount });
    setDraft({ description: "", date: "", amount: "" });
  };

  return (
    <div className={`card shadow-sm ${styles.BudgetCard}`}>
      <div className="card-body">
        {/* header (click to toggle) */}
        <div className="d-flex align-items-center justify-content-between" role="button" onClick={onToggle}>
          <div>
            <div className="fw-semibold">{item.name}</div>
            <div className="text-muted small">
              {daysLabel} • target {new Date(item.targetDate).toLocaleDateString()}
            </div>
          </div>
          <div className="text-end">
            <div className="fw-semibold">{fmtMoney(total, currencyCode)}</div>
            <div className={`badge text-bg-${item?._computed?.feasibility?.level || "secondary"}`}>
              {item?._computed?.feasibility?.label || "—"}
            </div>
          </div>
        </div>

        {/* progress */}
        <div className="mt-3">
          <div className="progress" role="progressbar" aria-valuenow={percent}>
            <div className={`progress-bar bg-${barVariant}`} style={{ width: `${percent}%` }}>
              <span className="ms-2">
                {fmtMoney(allocated, currencyCode)} of {fmtMoney(total, currencyCode)} •
                {" "}remain {fmtMoney(remaining, currencyCode)}
              </span>
            </div>
          </div>
          <div className="d-flex justify-content-between text-muted small mt-1">
            <div>Buffer up to {fmtMoney(bufferMax, currencyCode)} ({item.bufferPct}% margin)</div>
            <div>{allocated <= bufferMax ? "Within buffer" : "Over buffer"}</div>
          </div>
        </div>

        {/* tags + source */}
        <div className="d-flex flex-wrap gap-2 mt-3">
          {(item.tags || []).map((t) => (
            <span key={t} className="badge text-bg-secondary">#{t}</span>
          ))}
          {Array.isArray(item.budgetTagPath) && item.budgetTagPath.length > 0 && (
            <span className="badge text-bg-info">{item.budgetTagPath[0]}</span>
          )}
          <span className="badge text-bg-light border">{accountName}</span>
        </div>

        {/* expanded */}
        {expanded && (
          <div className="mt-4">
            {item.description ? <p className="mb-3">{item.description}</p> : null}

            <div className="row g-3">
              <div className="col-lg-6">
                <h6 className="mb-2">Planned transactions</h6>
                {plannedTransactions.length === 0 ? (
                  <div className="text-muted small">No planned transactions yet.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "40%" }}>Description</th>
                          <th style={{ width: "25%" }}>Date</th>
                          <th style={{ width: "20%" }} className="text-end">Amount</th>
                          <th style={{ width: "15%" }} />
                        </tr>
                      </thead>
                      <tbody>
                        {plannedTransactions
                          .slice()
                          .sort((a, b) => new Date(a.date) - new Date(b.date) || String(a.description).localeCompare(String(b.description)))
                          .map((tx) => (
                            <tr key={tx.id}>
                              <td className="truncate">{tx.description}</td>
                              <td>{new Date(tx.date).toLocaleDateString()}</td>
                              <td className="text-end">{fmtMoney(Math.abs(num(tx.amount ?? tx.amountAbs)), currencyCode)}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => onDeletePlannedTx?.(item.id, tx.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="col-lg-6">
                <h6 className="mb-2">Add planned transaction</h6>
                <div className="row g-2">
                  <div className="col-12">
                    <input
                      className="form-control"
                      placeholder="e.g., Flights"
                      value={draft.description}
                      onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="date"
                      className="form-control"
                      value={draft.date}
                      onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      placeholder="Amount"
                      value={draft.amount}
                      onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                    />
                  </div>
                  <div className="col-12">
                    <button type="button" className="btn btn-outline-primary btn-compact" onClick={addTx}>
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted small">
                Created{" "}
                {item?.createdAt?.toDate
                  ? item.createdAt.toDate().toLocaleString()
                  : new Date(item?.createdAt || Date.now()).toLocaleString()}
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-danger" onClick={onDelete}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

BudgetCard.propTypes = {
  item: PropTypes.object.isRequired,
  plannedTransactions: PropTypes.array,
  accounts: PropTypes.array,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onDelete: PropTypes.func,
  onAddPlannedTx: PropTypes.func,
  onDeletePlannedTx: PropTypes.func,
};
